import { useState, useEffect } from 'react';
import { ethers } from 'ethers6';
import { executeWithRpcFailover } from '../utils/rpcProvider';
import { supabase } from '../utils/supabase';
import type { PoolWithTVL } from './useMavrkPools';

/**
 * Hook to calculate TVL for individual pools
 * 
 * Uses Reservoir's approach (similar to their GraphQL API):
 * 1. Get token0 and token1 balances from pool contract
 * 2. Get prices for both tokens from Supabase token_prices table (cached by cron job)
 * 3. Calculate TVL = (balance0 * price0) + (balance1 * price1)
 * 
 * This is more accurate than WETH balance * 2 because:
 * - Accounts for actual token amounts in the pool
 * - Works for any token pair (not just WETH pairs)
 * - Matches what Reservoir's GraphQL returns (token0Supply, token1Supply)
 */

const ERC20_ABI = [
  {
    "constant": true,
    "inputs": [{ "name": "account", "type": "address" }],
    "name": "balanceOf",
    "outputs": [{ "name": "", "type": "uint256" }],
    "type": "function"
  },
  {
    "constant": true,
    "inputs": [],
    "name": "decimals",
    "outputs": [{ "name": "", "type": "uint8" }],
    "type": "function"
  }
];

const POOL_ABI = [
  {
    "constant": true,
    "inputs": [],
    "name": "token0",
    "outputs": [{ "name": "", "type": "address" }],
    "type": "function"
  },
  {
    "constant": true,
    "inputs": [],
    "name": "token1",
    "outputs": [{ "name": "", "type": "address" }],
    "type": "function"
  }
];

// Cache for TVL values (5 minute cache)
const tvlCache = new Map<string, { tvl: number; timestamp: number }>();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Calculate TVL for a single pool using Reservoir's approach
 * Gets actual token balances and prices (more accurate than WETH * 2)
 */
const calculatePoolTVL = async (poolAddress: string): Promise<number> => {
  // Check cache
  const cached = tvlCache.get(poolAddress);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return cached.tvl;
  }

  try {
    return await executeWithRpcFailover(async (provider) => {
      const pool = new ethers.Contract(poolAddress, POOL_ABI, provider);
      
      // Get token addresses from pool
      const [token0Address, token1Address] = await Promise.all([
        pool.token0(),
        pool.token1()
      ]);
      
      // Get token contracts
      const token0 = new ethers.Contract(token0Address, ERC20_ABI, provider);
      const token1 = new ethers.Contract(token1Address, ERC20_ABI, provider);
      
      // Get balances and decimals
      const [balance0, balance1, decimals0, decimals1] = await Promise.all([
        token0.balanceOf(poolAddress),
        token1.balanceOf(poolAddress),
        token0.decimals(),
        token1.decimals()
      ]);
      
      // Convert to human-readable amounts
      const amount0 = Number(ethers.formatUnits(balance0, decimals0));
      const amount1 = Number(ethers.formatUnits(balance1, decimals1));
      
      // Get token prices from Supabase token_prices table (cached by cron job)
      const INK_CHAIN_ID = 57073;
      const { data: pricesData } = await supabase
        .from('token_prices')
        .select('contract_address, price_usd')
        .in('contract_address', [token0Address.toLowerCase(), token1Address.toLowerCase()])
        .eq('chain_id', INK_CHAIN_ID);
      
      const priceMap = new Map<string, number>();
      pricesData?.forEach(p => {
        priceMap.set(p.contract_address.toLowerCase(), p.price_usd);
      });
      
      const price0 = priceMap.get(token0Address.toLowerCase()) || 0;
      const price1 = priceMap.get(token1Address.toLowerCase()) || 0;
      
      // Calculate TVL = (amount0 * price0) + (amount1 * price1)
      const tvl = (amount0 * (price0 || 0)) + (amount1 * (price1 || 0));
      
      // Cache the result
      tvlCache.set(poolAddress, { tvl, timestamp: Date.now() });
      
      return tvl;
    });
  } catch (error) {
    console.warn(`[usePoolTVL] Error calculating TVL for pool ${poolAddress}:`, error);
    return 0;
  }
};

/**
 * Calculate TVL for multiple pools in batch using Reservoir's approach
 */
const calculatePoolsTVL = async (pools: PoolWithTVL[]): Promise<Map<string, number>> => {
  const tvlMap = new Map<string, number>();
  
  // Filter pools that have pool addresses
  const poolsWithAddresses = pools.filter(p => p.pool_address);
  
  if (poolsWithAddresses.length === 0) {
    return tvlMap;
  }

  try {
    return await executeWithRpcFailover(async (provider) => {
      const INK_CHAIN_ID = 57073;
      
      // First, collect all unique token addresses we'll need prices for
      const tokenAddresses = new Set<string>();
      const poolTokenMap = new Map<string, { token0: string; token1: string }>();
      
      // Get all token addresses from pools
      for (const pool of poolsWithAddresses) {
        if (!pool.pool_address) continue;
        
        try {
          const poolContract = new ethers.Contract(pool.pool_address, POOL_ABI, provider);
          const [token0Address, token1Address] = await Promise.all([
            poolContract.token0(),
            poolContract.token1()
          ]);
          
          tokenAddresses.add(token0Address.toLowerCase());
          tokenAddresses.add(token1Address.toLowerCase());
          poolTokenMap.set(pool.pool_address, {
            token0: token0Address.toLowerCase(),
            token1: token1Address.toLowerCase()
          });
        } catch (error) {
          console.warn(`[usePoolTVL] Error getting tokens for pool ${pool.pool_address}:`, error);
        }
      }
      
      // Batch fetch all prices from Supabase in one query (much more efficient!)
      const { data: pricesData } = await supabase
        .from('token_prices')
        .select('contract_address, price_usd')
        .in('contract_address', Array.from(tokenAddresses))
        .eq('chain_id', INK_CHAIN_ID);
      
      // Create price map for quick lookup
      const priceMap = new Map<string, number>();
      pricesData?.forEach(p => {
        priceMap.set(p.contract_address.toLowerCase(), p.price_usd);
      });
      
      // Process pools in batches to respect RPC limits
      const batchSize = 10; // Can increase since we're not calling Relay API per pool
      for (let i = 0; i < poolsWithAddresses.length; i += batchSize) {
        const batch = poolsWithAddresses.slice(i, i + batchSize);
        
        const batchPromises = batch.map(async (pool) => {
          if (!pool.pool_address) return null;
          
          try {
            // Check cache first
            const cached = tvlCache.get(pool.pool_address);
            if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
              return { poolAddress: pool.pool_address, tvl: cached.tvl };
            }
            
            const tokens = poolTokenMap.get(pool.pool_address);
            if (!tokens) return null;
            
            const poolContract = new ethers.Contract(pool.pool_address, POOL_ABI, provider);
            
            // Get token contracts
            const token0 = new ethers.Contract(tokens.token0, ERC20_ABI, provider);
            const token1 = new ethers.Contract(tokens.token1, ERC20_ABI, provider);
            
            // Get balances and decimals
            const [balance0, balance1, decimals0, decimals1] = await Promise.all([
              token0.balanceOf(pool.pool_address),
              token1.balanceOf(pool.pool_address),
              token0.decimals(),
              token1.decimals()
            ]);
            
            // Convert to human-readable amounts
            const amount0 = Number(ethers.formatUnits(balance0, decimals0));
            const amount1 = Number(ethers.formatUnits(balance1, decimals1));
            
            // Get prices from Supabase cache (already fetched above)
            const price0 = priceMap.get(tokens.token0) || 0;
            const price1 = priceMap.get(tokens.token1) || 0;
            
            // Calculate TVL = (amount0 * price0) + (amount1 * price1)
            const tvl = (amount0 * price0) + (amount1 * price1);
            
            // Cache the result
            tvlCache.set(pool.pool_address, { tvl, timestamp: Date.now() });
            
            return { poolAddress: pool.pool_address, tvl };
          } catch (error) {
            console.warn(`[usePoolTVL] Error for pool ${pool.pool_address}:`, error);
            return { poolAddress: pool.pool_address, tvl: 0 };
          }
        });
        
        const results = await Promise.all(batchPromises);
        results.forEach(result => {
          if (result) {
            tvlMap.set(result.poolAddress, result.tvl);
          }
        });
        
        // Small delay between batches
        if (i + batchSize < poolsWithAddresses.length) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }
      
      return tvlMap;
    });
  } catch (error) {
    console.error('[usePoolTVL] Error calculating pools TVL:', error);
    return tvlMap;
  }
};

/**
 * Hook to get TVL for a single pool (on-demand calculation)
 * Use this when a pool is selected to avoid calculating TVL for all pools
 */
export const useSinglePoolTVL = (poolAddress: string | null | undefined) => {
  const [tvl, setTvl] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const fetchTVL = async () => {
      if (!poolAddress) {
        setTvl(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const calculatedTVL = await calculatePoolTVL(poolAddress);
        if (isMounted) {
          setTvl(calculatedTVL);
          setError(null);
        }
      } catch (err) {
        if (isMounted) {
          const errorMessage = err instanceof Error ? err.message : 'Failed to calculate TVL';
          console.error('[useSinglePoolTVL] Error:', errorMessage);
          setError(errorMessage);
          setTvl(null);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchTVL();

    return () => {
      isMounted = false;
    };
  }, [poolAddress]);

  return {
    tvl,
    loading,
    error,
    refetch: async () => {
      if (!poolAddress) return;
      setLoading(true);
      const calculatedTVL = await calculatePoolTVL(poolAddress);
      setTvl(calculatedTVL);
      setLoading(false);
    }
  };
};

/**
 * Hook to get TVL for a list of pools (batch calculation)
 * @deprecated Use useSinglePoolTVL for better performance when only one pool is needed
 */
export const usePoolTVL = (pools: PoolWithTVL[]) => {
  const [tvlMap, setTvlMap] = useState<Map<string, number>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const fetchTVL = async () => {
      if (pools.length === 0) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const tvl = await calculatePoolsTVL(pools);
        if (isMounted) {
          setTvlMap(tvl);
          setError(null);
        }
      } catch (err) {
        if (isMounted) {
          const errorMessage = err instanceof Error ? err.message : 'Failed to calculate TVL';
          console.error('[usePoolTVL] Error:', errorMessage);
          setError(errorMessage);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchTVL();

    return () => {
      isMounted = false;
    };
  }, [pools]);

  const getTVL = (poolAddress: string | null | undefined): number => {
    if (!poolAddress) return 0;
    return tvlMap.get(poolAddress) || 0;
  };

  return {
    tvlMap,
    loading,
    error,
    getTVL,
    refetch: async () => {
      setLoading(true);
      const tvl = await calculatePoolsTVL(pools);
      setTvlMap(tvl);
      setLoading(false);
    }
  };
};

